import { forkJoin, map, Observable, of } from 'rxjs';

import { CelumPropertiesProvider, hasValue, isEmpty } from '@celum/core';
import {
  AnyOf,
  FileExtensionGroup,
  FormatSpecificationReference,
  Options,
  Process,
  SpecificationContract,
  SpecificationParameter
} from '@celum/formats/domain';

import { ContractReferenceProvider } from '../model';

export const X_CELUM_UI_PARAMETERS_METADATA = 'x-celum-ui-parameters-metadata';

/**
 * Pure/dependency-free helpers for parsing a `SpecificationContract` (as returned by `SpecificationsService`) into
 * the flattened `SpecificationParameter[]`/`FileExtensionGroup[]` shape the format/base-format view dialogs render.
 * Used by the single `SpecificationStore` implementation regardless of whether it's backed by `SpecificationsService`
 * (tenant-scoped formats) or `SpecificationDefinitionsService` (base formats).
 */
export function mergeProcessRefsInContract(contract: SpecificationContract, specService: ContractReferenceProvider): Observable<SpecificationContract> {
  const ref = contract.components.schemas.Process.allOf.find(process => process.$ref)?.$ref;
  if (!ref || !ref.includes('://')) {
    return of(contract);
  }

  const path = ref.split('#')[1].split('/').filter(hasValue);
  return specService.getContractReference(ref).pipe(
    map(refContract => {
      const baseType = getValueFromObject(refContract, path)?.allOf.find((process: Process) => process.properties);
      const processWithProperties = contract.components.schemas.Process.allOf.find(process => process.properties);

      mergeFilters(processWithProperties, baseType);
      mergeParameters(processWithProperties, baseType);

      return contract;
    })
  );
}

export function mergeFilters(processWithProperties: Process, baseType: Process): void {
  if (!baseType?.properties?.filters?.properties) {
    return;
  }

  const filterProps = processWithProperties?.properties?.filters?.properties;
  if (!hasValue(filterProps?.fileExtensions?.default)) {
    processWithProperties.properties = {
      ...processWithProperties.properties,
      filters: baseType.properties.filters
    };
  }
}

export function mergeParameters(processWithProperties: Process, baseType: Process): void {
  if (!baseType?.properties?.parameters?.properties || !processWithProperties?.properties?.parameters?.properties) {
    return;
  }

  processWithProperties.properties.parameters.properties = {
    ...baseType.properties.parameters.properties,
    ...processWithProperties.properties.parameters.properties
  };
}

export function resolveExternalRefs(contract: SpecificationContract, specService: ContractReferenceProvider): Observable<SpecificationContract> {
  const paramProperties = contract.components.schemas.Process.allOf.find((process: Process) => process.properties)?.properties?.parameters?.properties;
  if (!paramProperties) {
    return of(contract);
  }

  const unresolvedRefs = collectUnresolvedRefs(paramProperties, contract);
  if (unresolvedRefs.length === 0) {
    return of(contract);
  }

  const refRequests = buildRefRequests(unresolvedRefs, specService);
  return forkJoin(refRequests).pipe(
    map(fetchedRefContracts => {
      applyResolvedRefs(paramProperties, unresolvedRefs, fetchedRefContracts);
      return contract;
    })
  );
}

function collectUnresolvedRefs(paramProperties: Record<string, any>, contract: SpecificationContract): { key: string; ref: string }[] {
  const unresolvedRefs: { key: string; ref: string }[] = [];

  for (const [key, value] of Object.entries(paramProperties)) {
    if (value?.$ref && value.$ref.includes('://') && value.$ref.includes('#')) {
      const path = resolveRefPath(value.$ref);
      const localResolved = getValueFromObject(contract, path);
      if (!localResolved) {
        unresolvedRefs.push({ key, ref: value.$ref });
      }
    }
  }

  return unresolvedRefs;
}

function buildRefRequests(
  unresolvedRefs: { key: string; ref: string }[],
  specService: ContractReferenceProvider
): Record<string, Observable<SpecificationContract>> {
  // a record of e.g. https://host/path/file.yaml -> Observable<SpecificationContract>
  const refRequests: Record<string, Observable<SpecificationContract>> = {};

  for (const { ref } of unresolvedRefs) {
    const baseUrl = ref.split('#')[0];
    if (!refRequests[baseUrl]) {
      refRequests[baseUrl] = specService.getContractReference(ref);
    }
  }

  return refRequests;
}

function applyResolvedRefs(
  paramProperties: Record<string, any>,
  unresolvedRefs: { key: string; ref: string }[],
  fetchedRefContracts: Record<string, SpecificationContract>
): void {
  for (const { key, ref } of unresolvedRefs) {
    const baseUrl = ref.split('#')[0];
    const path = resolveRefPath(ref);
    const refContract = fetchedRefContracts[baseUrl];
    const resolved = getValueFromObject(refContract, path);
    if (resolved) {
      paramProperties[key] = { ...resolveNestedRefs(resolved, refContract), key };
    }
  }
}

function resolveNestedRefs(schema: any, sourceContract: any): any {
  if (!schema || typeof schema !== 'object') {
    return schema;
  }

  const result = Array.isArray(schema) ? [...schema] : { ...schema };

  for (const [prop, value] of Object.entries(result) as [string, any][]) {
    if (value?.$ref && !value.$ref.includes('://')) {
      const refPath = resolveRefPath(value.$ref);
      const resolved = getValueFromObject(sourceContract, refPath);
      if (resolved) {
        result[prop] = resolveNestedRefs(resolved, sourceContract);
      }
    } else if (value && typeof value === 'object') {
      result[prop] = resolveNestedRefs(value, sourceContract);
    }
  }

  return result;
}

export function extractSpecFileExtensions(spec: SpecificationContract): FileExtensionGroup[] {
  const fileExtensions =
    spec.components.schemas.Process.allOf.find(process => process.properties)?.properties?.filters?.properties?.fileExtensions?.default || [];

  const groups: FileExtensionGroup[] = CelumPropertiesProvider.properties.appProperties.formats.fileExtensionsGroups;

  const unspecifiedExtensionsGroup = fileExtensions.filter(ext => !groups.find(group => group.extensions.includes(ext)));

  return [
    ...groups.map(({ name, extensions }) => ({ name, extensions: fileExtensions.filter(ext => extensions.includes(ext)) })),
    { name: 'FORMAT_VIEW_DIALOG.EXTENSIONS_GROUP.UNDEFINED', extensions: unspecifiedExtensionsGroup }
  ].filter(group => hasValue(group.extensions));
}

export function extractSpecParameters(spec: SpecificationContract): SpecificationParameter[] {
  const processWithProperties = spec.components.schemas.Process.allOf.find(process => process.properties);
  const optionsWithAdditionalProperties = processWithProperties?.properties?.parameters?.properties?.options?.allOf?.find(
    (properties: any) => properties?.additionalProperties
  );

  const directParameters = (extractAndFlattenDirectParams(spec, processWithProperties) ?? []).map(param => ({ ...param, parameterSource: 'direct' as const }));
  const optionsParameters = (optionsWithAdditionalProperties ? (extractOptionsParameters(spec, optionsWithAdditionalProperties) ?? []) : []).map(param => ({
    ...param,
    parameterSource: 'options' as const
  }));

  const allParameters = [...directParameters, ...optionsParameters];
  return allParameters.length > 0 ? allParameters : null;
}

export function extractOptionsParameters(spec: SpecificationContract, optionsWithAdditionalProperties: Options): SpecificationParameter[] {
  const additionalProperties: AnyOf[] = optionsWithAdditionalProperties?.properties?.values?.additionalProperties?.anyOf;

  if (additionalProperties) {
    let currentProp: string;
    return additionalProperties.reduce<SpecificationParameter[]>((acc, property) => {
      if (property?.$ref) {
        const path = resolveRefPath(property.$ref);
        currentProp = path[path.length - 1];
        acc.push(normalizeUiMetadata({ ...getValueFromObject(spec, path), key: currentProp }));
      }

      if (property?.default && currentProp) {
        acc[acc.length - 1].default = property.default;
      }

      return acc;
    }, []);
  } else {
    const properties = optionsWithAdditionalProperties?.properties?.values?.properties;

    return Object.keys(properties || {}).map(key => normalizeUiMetadata({ ...properties[key], key }));
  }
}

export function extractAndFlattenDirectParams(spec: SpecificationContract, processWithProperties: Process): SpecificationParameter[] {
  const paramProperties = processWithProperties?.properties?.parameters?.properties;
  if (!paramProperties) {
    return null;
  }

  const result: SpecificationParameter[] = [];
  for (const [key, value] of Object.entries(paramProperties)) {
    result.push(...resolveDirectParam(spec, key, value));
  }

  return result.length > 0 ? result : null;
}

function resolveDirectParam(spec: SpecificationContract, key: string, value: any): SpecificationParameter[] {
  if (value?.$ref) {
    const resolved = getValueFromObject(spec, resolveRefPath(value.$ref));
    return resolved && hasUiMetadata(resolved) ? flattenParameter(key, resolved) : [];
  }

  return value?.type && hasUiMetadata(value) ? flattenParameter(key, value) : [];
}

export function hasUiMetadata(param: any): boolean {
  if (param?.[X_CELUM_UI_PARAMETERS_METADATA]) {
    return true;
  }
  if (param?.properties) {
    return Object.values(param.properties).some((sub: any) => hasUiMetadata(sub));
  }
  return false;
}

export function flattenParameter(key: string, param: any): SpecificationParameter[] {
  if (param?.type === 'object' && param?.properties) {
    const results: SpecificationParameter[] = [];
    for (const [subKey, subValue] of Object.entries(param.properties) as [string, any][]) {
      results.push(...flattenParameter(`${key}_${subKey}`, subValue));
    }
    return results;
  }

  return [normalizeUiMetadata({ ...param, key })];
}

export function resolveRefPath(ref: string): string[] {
  const fragment = ref.includes('#') ? ref.split('#')[1] : ref;
  return fragment.split('/').filter(hasValue);
}

export function getValueFromObject(obj: any, keys: string[]): any {
  return keys.reduce((acc, key) => acc && acc[key], obj);
}

export function unwrapAllOfProperties(allOfArray: any[]): any {
  return allOfArray.find(entry => entry?.properties)?.properties ?? undefined;
}

export function normalizeUiMetadata(param: any): any {
  const metadata = param?.[X_CELUM_UI_PARAMETERS_METADATA];
  if (!metadata) {
    return param;
  }

  if (metadata.allOf && Array.isArray(metadata.allOf)) {
    const merged: any = {};
    for (const entry of metadata.allOf) {
      if (entry.$ref) {
        // $ref is just the type definition in the specification yaml
        continue;
      }

      Object.assign(merged, entry.properties);
    }

    // some nested metadata properties (e.g. dependsOn, dropdown) are themselves allOf arrays containing a $ref alongside the actual object with properties
    // we need to unwrap them
    if (Array.isArray(merged.dependsOn)) {
      merged.dependsOn = unwrapAllOfProperties(merged.dependsOn);
    }
    if (Array.isArray(merged.dropdown)) {
      merged.dropdown = unwrapAllOfProperties(merged.dropdown);
    }

    delete merged.type;
    return { ...param, 'x-celum-ui-parameters-metadata': merged };
  }

  return param;
}

export function getSpecKey(
  specFromStore: { key: string } | undefined,
  specFromFormat: FormatSpecificationReference | { key?: string }
): {
  key: string;
} {
  if (specFromStore) {
    return { key: specFromStore.key };
  }

  return { key: specFromFormat.key };
}

export function extractGroups(specs: SpecificationParameter[]): string[] {
  if (isEmpty(specs)) {
    return [];
  }

  const groups = specs.reduce<string[]>((acc, spec) => {
    if (!isEmpty(spec[X_CELUM_UI_PARAMETERS_METADATA]?.group?.default)) {
      acc.push(...spec[X_CELUM_UI_PARAMETERS_METADATA].group.default);
    }
    return acc;
  }, []);

  return Array.from(new Set(groups)); // Remove duplicates
}
